import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Medal, Shield, CheckCircle, Star, Trophy, ArrowRight,
  Certificate, Buildings, Sparkle, Download, Copy, Check
} from "@phosphor-icons/react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const CertificationBadgesPage = () => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const certifications = [
    {
      id: "survey-ready",
      name: "Survey Ready Certified",
      description: "Awarded to organizations that have completed our comprehensive Survey Readiness Program and demonstrated excellence in compliance preparation.",
      requirements: [
        "Completed Survey Readiness Assessment",
        "Addressed all identified compliance gaps",
        "Passed Mock Survey with no major deficiencies",
        "Implemented sustainable compliance processes"
      ],
      color: "from-emerald-500 to-teal-600",
      icon: Shield,
      badgeCode: `<a href="https://noblehospicesolutions.com/certifications/survey-ready" target="_blank"><img src="https://noblehospicesolutions.com/badges/survey-ready.svg" alt="Noble Healthcare Survey Ready Certified" width="150" /></a>`
    },
    {
      id: "qapi-excellence",
      name: "QAPI Excellence Certified",
      description: "Recognizes organizations that have built exemplary Quality Assessment and Performance Improvement programs exceeding CMS requirements.",
      requirements: [
        "Established comprehensive QAPI program",
        "Minimum 3 successful PIPs completed",
        "Demonstrated measurable quality improvements",
        "Staff engagement in quality initiatives"
      ],
      color: "from-blue-500 to-indigo-600",
      icon: Trophy,
      badgeCode: `<a href="https://noblehospicesolutions.com/certifications/qapi-excellence" target="_blank"><img src="https://noblehospicesolutions.com/badges/qapi-excellence.svg" alt="Noble Healthcare QAPI Excellence Certified" width="150" /></a>`
    },
    {
      id: "operations-optimized",
      name: "Operations Optimized",
      description: "Awarded to organizations that have successfully implemented operational improvements resulting in measurable efficiency gains.",
      requirements: [
        "Completed Operations Assessment",
        "Implemented workflow optimizations",
        "Achieved documented efficiency improvements",
        "Sustained improvements for 6+ months"
      ],
      color: "from-purple-500 to-violet-600",
      icon: Star,
      badgeCode: `<a href="https://noblehospicesolutions.com/certifications/operations-optimized" target="_blank"><img src="https://noblehospicesolutions.com/badges/operations-optimized.svg" alt="Noble Healthcare Operations Optimized" width="150" /></a>`
    },
    {
      id: "ai-enabled",
      name: "AI-Enabled Operations",
      description: "Recognizes organizations that have successfully integrated AI-powered tools to enhance operational intelligence and decision-making.",
      requirements: [
        "Implemented AI-powered operational tools",
        "Staff trained on system utilization",
        "Demonstrated data-driven decision making",
        "Achieved efficiency gains through automation"
      ],
      color: "from-orange-500 to-red-500",
      icon: Sparkle,
      badgeCode: `<a href="https://noblehospicesolutions.com/certifications/ai-enabled" target="_blank"><img src="https://noblehospicesolutions.com/badges/ai-enabled.svg" alt="Noble Healthcare AI-Enabled Operations" width="150" /></a>`
    }
  ];

  const benefits = [
    {
      title: "Build Trust",
      description: "Display your certification to demonstrate commitment to excellence to patients, families, and referral sources."
    },
    {
      title: "Stand Out",
      description: "Differentiate your organization from competitors with recognized quality certifications."
    },
    {
      title: "Attract Referrals",
      description: "Healthcare partners prefer working with certified, quality-focused organizations."
    },
    {
      title: "Recruit Talent",
      description: "Quality certifications attract staff who want to work for excellence-driven organizations."
    }
  ];

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div data-testid="certifications-page" className="min-h-screen bg-gradient-to-b from-muted/30 to-white">
      {/* Hero */}
      <section className="py-16 bg-hero-gradient relative overflow-hidden">
        <div className="decorative-blob w-96 h-96 bg-primary/20 -top-48 -right-48" />
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="badge badge-primary mb-4">
              <Medal weight="fill" className="w-4 h-4 mr-2" />
              Recognition Program
            </span>
            <h1 className="font-display text-4xl md:text-5xl text-foreground mb-4">
              Certification Badges
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Earn recognition for your organization's commitment to excellence. 
              Display Noble Healthcare certification badges on your website to 
              showcase your achievements.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle weight="fill" className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-1">{benefit.title}</h3>
                <p className="text-sm text-muted-foreground">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-12 bg-muted/30">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-display text-2xl text-foreground mb-8 text-center">
            Available Certifications
          </h2>

          <div className="space-y-8">
            {certifications.map((cert, idx) => (
              <motion.div
                key={cert.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-lg border border-border overflow-hidden"
              >
                <div className="flex flex-col lg:flex-row">
                  {/* Badge Preview */}
                  <div className={`lg:w-72 p-8 bg-gradient-to-br ${cert.color} flex flex-col items-center justify-center text-white`}>
                    <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center mb-4">
                      <cert.icon weight="fill" className="w-12 h-12" />
                    </div>
                    <h3 className="font-display text-xl text-center">{cert.name}</h3>
                    <p className="text-white/80 text-sm mt-2">Noble Healthcare Certified</p>
                  </div>

                  {/* Details */}
                  <div className="flex-1 p-6 lg:p-8">
                    <h3 className="font-display text-xl text-foreground mb-3">{cert.name}</h3>
                    <p className="text-muted-foreground mb-4">{cert.description}</p>

                    <h4 className="font-semibold text-foreground mb-2">Requirements:</h4>
                    <ul className="space-y-2 mb-6">
                      {cert.requirements.map((req, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle weight="fill" className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          {req}
                        </li>
                      ))}
                    </ul>

                    <div className="bg-muted/50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-foreground">Embed Code</h4>
                        <button
                          onClick={() => copyToClipboard(cert.badgeCode)}
                          className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
                        >
                          {copied ? (
                            <>
                              <Check weight="bold" className="w-4 h-4" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy weight="bold" className="w-4 h-4" />
                              Copy Code
                            </>
                          )}
                        </button>
                      </div>
                      <pre className="text-xs text-muted-foreground bg-white rounded-lg p-3 overflow-x-auto">
                        {cert.badgeCode}
                      </pre>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Earn */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-2xl text-foreground mb-8 text-center">
            How to Earn Your Certification
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { step: "1", title: "Engage", desc: "Partner with Noble Healthcare on a relevant service engagement" },
              { step: "2", title: "Complete", desc: "Successfully complete the program requirements with documented results" },
              { step: "3", title: "Display", desc: "Receive your badge and display it proudly on your website" }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-muted/30">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Certificate weight="fill" className="w-12 h-12 text-accent mx-auto mb-4" />
          <h2 className="font-display text-2xl text-foreground mb-3">
            Ready to Get Certified?
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-6">
            Start your journey to certification with a free consultation. 
            We'll assess your current state and recommend the right path forward.
          </p>
          <Link to="/schedule-demo" className="btn-primary inline-flex items-center gap-2">
            Start Your Certification Journey
            <ArrowRight weight="bold" className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CertificationBadgesPage;
